import { TutorialConfig, TutorialStep } from "../types";

export const musescoreTutorialConfig: TutorialConfig = {
  software: "musescore",
  displayName: "MuseScore Tutorial",
  totalTasks: 6,
  completionKey: "musescore-tutorial-completed",
  dataSource: "api",

  tasks: [
    {
      id: "obvious-success",
      name: "Add parallel thirds",
      type: "success",
      runId: "df1c5215-c625-4691-93f4-4de4fa71e62a",
      description:
        "The AI completed the task in a few steps and used the correct tools. A clear success.",
    },
    {
      id: "obvious-failure",
      name: "Transcribe a chorale to a piano part",
      type: "failure",
      runId: "76fece12-85fb-44dc-91c8-38cbca922623",
      description:
        "The AI did not copy all the notes and also introduced incorrect and awkward rhythms, failing both musically and functionally. This is a clear example of a task that's too long for the AI. It was nowhere close to successfully finishing it.",
    },
    {
      id: "looks-like-success-but-failure-1",
      name: "Copy music",
      type: "fake_success",
      runId: "dddeee08-7f4c-4826-aba3-ea3c878c2061",
      description:
        "At first glance, it looks like the AI copied the music correctly. However, a closer look at the keys and clicks used shows that it only copied the first system and did not complete the full score. Remember, sometimes you can't see the full score in the video! This is a great example for when you would want to download the MuseScore file (right click the video) to verify whether the AI was successful or not.",
    },
    {
      id: "looks-like-success-but-failure-2",
      name: "Add articulations",
      type: "fake_success",
      runId: "20bd0c14-2770-459d-9f50-e1f0ccbf4125",
      description:
        "The AI added staccato markings to the eighth notes and accents to the quarter notes, as requested. However, it also moved a flat on one of the notes in the violin 2 part and also added an accent to a half note in the violoncello part. These additional modifications make the run a failure.",
    },
    {
      id: "looks-like-failure-but-success-1",
      name: "Task 29",
      type: "fake_failure",
      runId: "e839345a-9208-475c-a4c6-4eaef7abad72",
      description:
        "This run shows the AI taking a very roundabout approach: First, it moved the score view, zoomed out, and clicked several unnecessary buttons. Then it made a mistake by replacing the two eighth notes with a quarter rest. However, it recognized its error and corrected it, ultimately adding the E3 correctly with no additional modifications. Despite the inefficient process and initial mistake, the final result matches the task requirements exactly - making this a success!",
    },
    {
      id: "ambiguous-bad-task",
      name: "Provide a pickup measure",
      type: "bad_task",
      runId: "faa0ebaf-f748-4823-9e6a-ad2743177d6c",
      description:
        "This task asks to 'Provide a pickup measure with music that leads into measure 1.' However, it doesn't specify how many beats the pickup should contain, what the musical material should be (rhythm, pitch, instrumentation), or any other parameters. Adding a pickup measure has many valid interpretations, making this task extremely ambiguous and impossible to evaluate objectively.",
    },
  ],

  steps: [
    {
      id: "welcome",
      title: "Welcome to MuseScore Evaluation!",
      content:
        "Welcome! In this tutorial, you'll learn how to evaluate AI attempts at completing MuseScore tasks. You'll watch videos of AI agents working in MuseScore and determine whether they successfully completed the given task.",
      targetSelector: ".App-header h1",
      action: "next",
      position: "bottom",
    },
    {
      id: "task-description",
      title: "Task Description",
      content:
        "This shows the task instructions given to the AI. Read this carefully to understand what the AI was supposed to do.",
      targetSelector: ".task-description",
      action: "next",
      position: "right",
    },
    {
      id: "skip-to-frame-2",
      title: "Skip to Frame 2",
      content:
        "Click here to skip to the next frame of the video. This is useful for quickly navigating through the AI's actions.",
      targetSelector: ".nav-button:nth-child(3)",
      action: "seek",
      position: "top",
    },
    {
      id: "current-action",
      title: "Current Action",
      content:
        "This section shows you what action the AI is currently performing. Pay attention to whether these actions make sense for the given task.",
      targetSelector: ".sidebar-section:nth-child(3)",
      action: "next",
      position: "right",
    },
    {
      id: "main-click",
      title: "Main Click Indicator",
      content:
        "The yellow circle shows where the AI clicked. Watch these clicks carefully to understand what the AI is trying to do.",
      targetSelector: ".click-indicator",
      action: "next",
      position: "left",
    },
    {
      id: "next-click",
      title: "Next Click Preview",
      content:
        "The dotted yellow circle shows where the AI will click next. This helps you anticipate the AI's actions.",
      targetSelector: ".next-click-indicator",
      action: "next",
      position: "left",
    },
    {
      id: "go-to-frame-3",
      title: "Go to Next Frame",
      content: "Click the next button again to advance to the next frame.",
      targetSelector: ".nav-button:nth-child(3)",
      action: "seek",
      position: "top",
    },
    {
      id: "seek-backward",
      title: "Previous Frame",
      content:
        "Use this button to go back to previous frames if you need to review something.",
      targetSelector: ".nav-button:first-child",
      action: "seek",
      position: "top",
    },
    {
      id: "play",
      title: "Play the Video",
      content:
        "Click here to play the video and watch the AI's full attempt. You can pause at any time.",
      targetSelector: ".play-pause-btn",
      action: "play",
      position: "top",
    },
    {
      id: "pause",
      title: "Pause the Video",
      content:
        "Click here to pause the video. This is useful when you need to examine something more closely.",
      targetSelector: ".play-pause-btn",
      action: "pause",
      position: "top",
    },
    {
      id: "speed-controls",
      title: "Speed Controls",
      content:
        "Adjust the playback speed here. Slower speeds help you catch details, while faster speeds help you review long tasks quickly.",
      targetSelector: ".speed-selector",
      action: "next",
      position: "top",
    },
    {
      id: "seek-timeline",
      title: "Timeline Scrubbing",
      content:
        "You can click and drag along the timeline to jump to any part of the video. The timeline shows you how far through the task the AI is.",
      targetSelector: ".timeline-container",
      action: "next",
      position: "top",
    },
    {
      id: "download-model",
      title: "Download MuseScore File",
      content:
        "Right-click on the video to download the MuseScore file. This is CRUCIAL for verifying the AI's work, especially when you can't see the full score in the video!",
      targetSelector: ".screenshot-container",
      action: "next",
      position: "left",
    },
    {
      id: "watch-before-eval",
      title: "Watch Before Evaluating",
      content:
        "IMPORTANT: Always watch the entire video before making your evaluation. The AI might correct mistakes or complete tasks later in the video.",
      targetSelector: ".sidebar-section:nth-child(2)",
      action: "next",
      position: "right",
    },
    {
      id: "finalframe",
      title: "Jump to Final Frame",
      content:
        "You can click at the end of the timeline to quickly jump to the final result. This helps you see what the AI ultimately produced.",
      targetSelector: ".timeline-container",
      action: "next",
      position: "top",
    },
    {
      id: "musescore-specific",
      title: "Special Tips for MuseScore",
      content:
        "MuseScore tasks require careful attention to musical details. Always check that notes, rhythms, and articulations are correct. Watch out for unintended changes like moved notes or extra markings. Most importantly, the AI must complete ALL requested changes - partial completion always counts as failure!",
      action: "next",
    },
    {
      id: "evaluation-time",
      title: "Time to Evaluate",
      content:
        "After watching the video, you'll use these buttons to evaluate the AI's performance. Let's look at what each option means.",
      targetSelector: ".evaluation-buttons",
      action: "next",
      position: "top",
    },
    {
      id: "success-button",
      title: "Success",
      content:
        "Click 'Success' if the AI completed the task correctly without any unwanted changes. The task must be FULLY completed.",
      targetSelector: ".evaluation-btn.success",
      action: "next",
      position: "top",
    },
    {
      id: "failure-button",
      title: "Failure",
      content:
        "Click 'Failure' if the AI didn't complete the task OR made unwanted changes. Even small errors count as failure.",
      targetSelector: ".evaluation-btn.fail",
      action: "next",
      position: "top",
    },
    {
      id: "bad-task-button",
      title: "Bad Task",
      content:
        "Click 'Bad Task' if the instructions were unclear, ambiguous, or impossible to complete. This is rare but important to identify.",
      targetSelector: ".evaluation-btn.bad-task",
      action: "next",
      position: "top",
    },
    {
      id: "evaluation-restriction",
      title: "Important: Watch to the End!",
      content:
        "You can only evaluate the task once you've reached the final frame. The evaluation buttons will be disabled until then. This ensures you see the complete attempt before judging.",
      targetSelector: ".evaluation-buttons",
      action: "next",
      position: "top",
    },
    {
      id: "progress-saved",
      title: "Your Progress is Saved",
      content:
        "You can exit anytime - your evaluations are saved automatically. Feel free to work at your own pace.",
      action: "next",
    },
    {
      id: "ready-to-start",
      title: "Ready to Start!",
      content:
        "Great! You now know how to use all the tools. Let's look at some real examples. Click 'Next' to see your first MuseScore task.",
      action: "next",
    },
    {
      id: "task-1-intro",
      title: "Example 1: Add Parallel Thirds",
      content:
        "In this task, the AI was asked to add parallel thirds to an existing melody. Watch the entire video carefully to see if it completes this task correctly.",
      targetSelector: ".task-description",
      action: "next",
      position: "right",
    },
    {
      id: "task-1-watch",
      title: "Watch the Complete Video",
      content:
        "Remember: You must watch the complete video to the end before you can evaluate. Pay attention to whether the AI adds thirds correctly without making unwanted changes.",
      targetSelector: ".screenshot-container",
      action: "next",
      position: "left",
    },
    {
      id: "task-1-evaluate",
      title: "Time to Evaluate Task 1",
      content:
        "The AI successfully added parallel thirds without any errors. Since the task was completed successfully, click the 'Success' button.",
      targetSelector: ".evaluation-btn.success",
      action: "evaluate",
      position: "top",
      expectedEvaluation: "success",
    },
    {
      id: "task-1-complete",
      title: "Example 1 Complete!",
      content:
        "Great job! You correctly identified this as a success. Click the 'Next task' button to continue to the next example.",
      targetSelector: "button.next-task",
      action: "next-task-button",
      position: "right",
    },
    {
      id: "task-2-intro",
      title: "Example 2: Transcribe a Chorale",
      content:
        "In this task, the AI was asked to transcribe a chorale to a piano part. This is a complex task - watch carefully to see how the AI handles it.",
      targetSelector: ".task-description",
      action: "next",
      position: "right",
    },
    {
      id: "task-2-watch",
      title: "Evaluate After Watching",
      content:
        "Remember: You must watch the complete video to the end before you can evaluate. Look for accuracy in transcription and rhythm.",
      targetSelector: ".screenshot-container",
      action: "next",
      position: "left",
    },
    {
      id: "task-2-evaluate",
      title: "Time to Evaluate Task 2",
      content:
        "The AI failed to copy all the notes and introduced incorrect rhythms. This is a clear failure. Click the 'Failure' button.",
      targetSelector: ".evaluation-btn.fail",
      action: "evaluate",
      position: "top",
      expectedEvaluation: "fail",
    },
    {
      id: "task-2-complete",
      title: "Example 2 Complete!",
      content:
        "Correct! This was indeed a failure. The task was too complex for the AI. Click 'Next task' to continue.",
      targetSelector: "button.next-task",
      action: "next-task-button",
      position: "right",
    },
    {
      id: "task-3-intro",
      title: "Example 3: Copy Music",
      content:
        "The AI was asked to copy music. This seems straightforward, but watch carefully - appearances can be deceiving!",
      targetSelector: ".task-description",
      action: "next",
      position: "right",
    },
    {
      id: "task-3-watch",
      title: "Watch for Completeness",
      content:
        "Pay special attention to whether the AI copies ALL the music. Remember: You can right-click to download the MuseScore file if needed!",
      targetSelector: ".screenshot-container",
      action: "next",
      position: "left",
    },
    {
      id: "task-3-evaluate",
      title: "Time to Evaluate Task 3",
      content:
        "The AI only copied the first system, not the full score. This partial completion is a failure. Click the 'Failure' button.",
      targetSelector: ".evaluation-btn.fail",
      action: "evaluate",
      position: "top",
      expectedEvaluation: "fail",
    },
    {
      id: "task-3-complete",
      title: "Example 3 Complete!",
      content:
        "Well done! You caught that it was only a partial completion. Click 'Next task' to continue.",
      targetSelector: "button.next-task",
      action: "next-task-button",
      position: "right",
    },
    {
      id: "task-4-intro",
      title: "Example 4: Add Articulations",
      content:
        "The AI was asked to add articulations to the music. Watch for both the requested changes AND any unintended modifications.",
      targetSelector: ".task-description",
      action: "next",
      position: "right",
    },
    {
      id: "task-4-watch",
      title: "Look for Extra Changes",
      content:
        "The AI might complete the main task but make unwanted changes. Watch the entire video carefully.",
      targetSelector: ".screenshot-container",
      action: "next",
      position: "left",
    },
    {
      id: "task-4-evaluate-part1",
      title: "Time to Evaluate Task 4 - Part 1",
      content:
        "The AI added the articulations as requested. However, it also moved a flat on one of the notes in the violin 2 part. Let's look at this error.",
      targetSelector: ".screenshot-container",
      action: "seek-frame",
      seekFrame: 6,
      position: "left",
    },
    {
      id: "task-4-evaluate-part2",
      title: "Time to Evaluate Task 4 - Part 2",
      content:
        "Additionally, the AI added an extra accent to a half note in the violoncello part. Let's look at this second error.",
      targetSelector: ".screenshot-container",
      action: "seek-frame",
      seekFrame: 22,
      position: "left",
    },
    {
      id: "task-4-evaluate",
      title: "Time to Evaluate Task 4",
      content:
        "The AI made two unwanted changes: moved a flat and added an extra accent. Any unwanted change = failure. Click 'Failure'.",
      targetSelector: ".evaluation-btn.fail",
      action: "evaluate",
      position: "top",
      expectedEvaluation: "fail",
    },
    {
      id: "task-4-complete",
      title: "Example 4 Complete!",
      content:
        "Excellent! You caught the unwanted changes. Even small errors count as failures. Click 'Next task'.",
      targetSelector: "button.next-task",
      action: "next-task-button",
      position: "right",
    },
    {
      id: "task-5-intro",
      title: "Example 5: Roundabout Success",
      content:
        "This example shows a task that looks messy but is actually successful. The AI takes a very indirect approach.",
      targetSelector: ".task-description",
      action: "next",
      position: "right",
    },
    {
      id: "task-5-watch",
      title: "Self-Correction Matters",
      content:
        "The AI makes a mistake by initially replacing eighth notes with a quarter rest, but then fixes it. Watch the complete video.",
      targetSelector: ".screenshot-container",
      action: "next",
      position: "left",
    },
    {
      id: "task-5-evaluate",
      title: "Time to Evaluate Task 5",
      content:
        "The AI made initial errors but corrected them, completing the task successfully. Click 'Success'.",
      targetSelector: ".evaluation-btn.success",
      action: "evaluate",
      position: "top",
      expectedEvaluation: "success",
    },
    {
      id: "task-5-complete",
      title: "Example 5 Complete!",
      content:
        "Perfect! You recognized that self-correction still counts as success. Click 'Next task' for one final example.",
      targetSelector: "button.next-task",
      action: "next-task-button",
      position: "right",
    },
    {
      id: "task-6-intro",
      title: "Example 6: Ambiguous Task",
      content:
        "This final example shows a 'bad task' - one that's too ambiguous to evaluate objectively. The AI is asked to add a pickup measure.",
      targetSelector: ".task-description",
      action: "next",
      position: "right",
    },
    {
      id: "task-6-watch",
      title: "Multiple Valid Interpretations",
      content:
        "As you watch, notice how the task doesn't specify: How many beats? What rhythm? What pitches? Which instruments? There are countless valid ways to add a pickup measure.",
      targetSelector: ".screenshot-container",
      action: "next",
      position: "left",
    },
    {
      id: "task-6-evaluate",
      title: "Time to Evaluate Task 6",
      content:
        "With no clear criteria for success, this task is impossible to evaluate objectively. Click 'Bad Task'.",
      targetSelector: ".evaluation-btn.bad-task",
      action: "evaluate",
      position: "top",
      expectedEvaluation: "bad-task",
    },
    {
      id: "task-6-complete",
      title: "Tutorial Complete!",
      content:
        "Excellent work! You've learned to identify successes, failures, and ambiguous tasks. Remember: always watch the entire video, download files when needed, and mark ambiguous tasks as 'Bad Task'. You're now ready to evaluate real MuseScore tasks. Good luck!",
      action: "next",
    },
  ] as TutorialStep[],
};
